// src/pages/PropertySearchPage.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import {SEARCH_PROPERTIES} from "../graphql/properties";

const PropertySearchPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearched, setIsSearched] = useState(false);
  
  const [searchProperties, { loading, error, data }] = useLazyQuery(SEARCH_PROPERTIES, {
    fetchPolicy: 'network-only', // Don't use cache for searches
  });
  
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim().length >= 2) {
      searchProperties({ variables: { searchTerm: searchTerm } });
      setIsSearched(true);
    }
  };
  
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-semibold mb-2">Property Search</h1>
        <p className="text-gray-600">
          Search for properties by reference number, address, suburb, or postal code.
        </p>
      </div>
      
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6 p-6">
        <form onSubmit={handleSearch}>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-grow">
              <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 mb-1">
                Search Term
              </label>
              <input
                type="text"
                id="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Reference number, address, suburb, or postal code"
                minLength={2}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter at least 2 characters to search
              </p>
            </div>
            
            <div className="self-end">
              <button
                type="submit"
                disabled={loading || searchTerm.trim().length < 2}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Searching...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Search results */}
      {isSearched && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Search Results
              {data?.searchProperties && (
                <span className="ml-2 text-sm text-gray-500">
                  ({data.searchProperties.length} properties found)
                </span>
              )}
            </h2>
          </div>
          
          {loading && (
            <div className="p-6 text-center">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-gray-500">Searching for properties...</p>
            </div>
          )}
          
          {error && (
            <div className="p-6">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {error.message}</span>
              </div>
            </div>
          )}
          
          {!loading && !error && data?.searchProperties?.length === 0 && (
            <div className="p-6 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No properties found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try using different search terms or check for typos.
              </p>
            </div>
          )}
          
          {!loading && !error && data?.searchProperties?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference #
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      People
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.searchProperties.map((property) => (
                    <tr key={property.id}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        {property.referenceNumber || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {property.address.street}
                        </div>
                        <div className="text-sm text-gray-500">
                          {property.address.suburb}, {property.address.town} {property.address.postalCode}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          (property.status === 'Rented By Us' || property.status === 'occupied') ? 'bg-green-100 text-green-800' : 
                          (property.status === 'Vacant Rental' || property.status === 'vacant') ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {(property.status === 'Rented By Us' || property.status === 'occupied') ? 'Occupied' : 
                          (property.status === 'Vacant Rental' || property.status === 'vacant') ? 'Vacant' : 
                          (property.status === 'Rental Application In Progress' || property.status === 'in-progress') ? 'In Progress' : 
                          property.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.landlord ? `${property.landlord.firstName} ${property.landlord.lastName}` : 'No Landlord'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {property.tenant ? `${property.tenant.firstName} ${property.tenant.lastName}` : 'No Tenant'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/properties/${property.id}`} className="text-blue-600 hover:text-blue-900 mr-4">
                          View
                        </Link>
                        <Link to={`/properties/edit/${property.id}`} className="text-indigo-600 hover:text-indigo-900">
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertySearchPage;